/**
 * The grid both sides look at.
 *
 * One component for the GM's board and the players' — the difference is which
 * tokens `draggableIds` contains. Rendering them from separate components
 * would let the two drift, and the whole point is that everyone is looking at
 * the same fight.
 *
 * Drag is pointer events on the SVG rather than a library: a token snaps to
 * whole squares, which is a rounding step, not a physics problem.
 */
import { useCallback, useRef, useState } from 'react';

import type { MapFeature, MapState, MapToken } from '@/api/battlemap';

const CELL = 38;
const PAD = 10;

const TERRAIN_FILL: Record<string, string> = {
  wall: 'var(--terrain-wall)',
  difficult: 'var(--terrain-difficult)',
  hazard: 'var(--terrain-hazard)',
  cover: 'var(--terrain-cover)',
  water: 'var(--terrain-water)',
  feature: 'var(--terrain-feature)',
};

const SIDE_FILL: Record<string, string> = {
  opposition: 'var(--side-opposition)',
  ally: 'var(--side-ally)',
  party: 'var(--side-party)',
  neutral: 'var(--side-neutral)',
};

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase();
}

export interface MapCanvasProps {
  state: MapState;
  /** Tokens this viewer may drag. Everything else is read-only. */
  draggableIds: string[];
  onMove?: (tokenId: string, x: number, y: number) => void;
  onSelect?: (token: MapToken | null) => void;
  selectedId?: string | null;
}

export function MapCanvas({
  state,
  draggableIds,
  onMove,
  onSelect,
  selectedId,
}: MapCanvasProps) {
  const [zoom, setZoom] = useState(1);
  const dragOrigin = useRef<{ id: string; x: number; y: number } | null>(null);
  const svgRef = useRef<SVGSVGElement | null>(null);
  const [dragging, setDragging] = useState<{
    id: string;
    x: number;
    y: number;
  } | null>(null);
  const [hovered, setHovered] = useState<MapFeature | null>(null);

  const width = state.width * CELL;
  const height = state.height * CELL;

  const cellFromEvent = useCallback(
    (e: { clientX: number; clientY: number }) => {
      const svg = svgRef.current;
      if (!svg) return null;
      // The fitted board may have letterboxing. The SVG transform maps through
      // both that offset and zoom; raw width ratios do not.
      const matrix = svg.getScreenCTM?.();
      if (matrix && svg.createSVGPoint) {
        const point = svg.createSVGPoint();
        point.x = e.clientX;
        point.y = e.clientY;
        const local = point.matrixTransform(matrix.inverse());
        return {
          x: Math.max(
            0,
            Math.min(state.width - 1, Math.floor((local.x - PAD) / CELL)),
          ),
          y: Math.max(
            0,
            Math.min(state.height - 1, Math.floor((local.y - PAD) / CELL)),
          ),
        };
      }
      const rect = svg.getBoundingClientRect();
      // The SVG is width-constrained and scales; map client pixels back
      // through the actual rendered size or a small screen drops tokens in
      // the wrong square.
      const scale = rect.width / (width + PAD * 2);
      if (scale <= 0) return null;
      const x = Math.floor((e.clientX - rect.left) / scale / CELL - PAD / CELL);
      const y = Math.floor((e.clientY - rect.top) / scale / CELL - PAD / CELL);
      return {
        x: Math.max(0, Math.min(state.width - 1, x)),
        y: Math.max(0, Math.min(state.height - 1, y)),
      };
    },
    [width, state.width, state.height],
  );

  return (
    <div className="mapcanvas">
      <div className="mapcanvas__viewport">
        <div className="mapcanvas__surface" style={{ width: `${zoom * 100}%` }}>
          <svg
            ref={svgRef}
            viewBox={`0 0 ${width + PAD * 2} ${height + PAD * 2}`}
            className="mapcanvas__svg"
            style={{ maxHeight: zoom === 1 ? '50vh' : undefined }}
            role="group"
            aria-label="Battle map"
            onPointerMove={(e) => {
              if (!dragging) return;
              const cell = cellFromEvent(e);
              if (cell && (cell.x !== dragging.x || cell.y !== dragging.y))
                setDragging({ ...dragging, ...cell });
            }}
            onPointerUp={() => {
              const origin = dragOrigin.current;
              if (
                dragging &&
                origin &&
                (dragging.x !== origin.x || dragging.y !== origin.y)
              )
                onMove?.(dragging.id, dragging.x, dragging.y);
              dragOrigin.current = null;
              setDragging(null);
            }}
            onPointerCancel={() => {
              dragOrigin.current = null;
              setDragging(null);
            }}
            onKeyDown={(event) => {
              if (event.key === 'Escape') {
                dragOrigin.current = null;
                setDragging(null);
              }
            }}
          >
            <g transform={`translate(${PAD} ${PAD})`}>
              <rect
                width={width}
                height={height}
                className="mapcanvas__ground"
              />

              {state.features.map((f, index) => (
                <g
                  key={f.id}
                  role="button"
                  tabIndex={0}
                  aria-label={`${f.name}: ${f.effect}`}
                  onPointerEnter={() => setHovered(f)}
                  onPointerLeave={() => setHovered(null)}
                  onFocus={() => setHovered(f)}
                  onBlur={() => setHovered(null)}
                  onClick={() =>
                    setHovered((current) => (current?.id === f.id ? null : f))
                  }
                  onKeyDown={(event) => {
                    if (event.key !== 'Enter' && event.key !== ' ') return;
                    event.preventDefault();
                    setHovered((current) => (current?.id === f.id ? null : f));
                  }}
                >
                  <rect
                    x={f.x * CELL}
                    y={f.y * CELL}
                    width={f.w * CELL}
                    height={f.h * CELL}
                    fill={TERRAIN_FILL[f.kind] ?? TERRAIN_FILL.feature}
                    className="mapcanvas__terrain"
                  />
                  <title>{`${f.name} — ${f.effect}`}</title>
                  <text
                    x={f.x * CELL + 4}
                    y={f.y * CELL + 13}
                    className="mapcanvas__terrain-label"
                  >
                    {index + 1}
                  </text>
                </g>
              ))}

              {Array.from({ length: state.width + 1 }, (_, i) => (
                <line
                  key={`v${i}`}
                  x1={i * CELL}
                  y1={0}
                  x2={i * CELL}
                  y2={height}
                  className="mapcanvas__grid"
                />
              ))}
              {Array.from({ length: state.height + 1 }, (_, i) => (
                <line
                  key={`h${i}`}
                  x1={0}
                  y1={i * CELL}
                  x2={width}
                  y2={i * CELL}
                  className="mapcanvas__grid"
                />
              ))}

              {[...state.tokens]
                .sort(
                  (a, b) =>
                    Number(a.id === selectedId) - Number(b.id === selectedId),
                )
                .map((t) => {
                  const live = dragging?.id === t.id ? dragging : t;
                  const draggable = draggableIds.includes(t.id);
                  return (
                    <g
                      key={t.id}
                      transform={`translate(${live.x * CELL + CELL / 2} ${live.y * CELL + CELL / 2})`}
                      className={[
                        'mapcanvas__token',
                        draggable ? 'mapcanvas__token--draggable' : '',
                        t.invisible ? 'mapcanvas__token--invisible' : '',
                        selectedId === t.id ? 'mapcanvas__token--selected' : '',
                      ].join(' ')}
                      role={draggable ? 'button' : 'img'}
                      tabIndex={draggable ? 0 : undefined}
                      aria-label={`${t.name}${t.tags.length ? `, ${t.tags.join(', ')}` : ''}${t.invisible ? ', hidden from players' : ''}`}
                      onPointerDown={(e) => {
                        onSelect?.(t);
                        if (
                          !draggable ||
                          (e.button !== undefined && e.button !== 0)
                        )
                          return;
                        dragOrigin.current = { id: t.id, x: t.x, y: t.y };
                        e.currentTarget.setPointerCapture?.(e.pointerId);
                        setDragging({ id: t.id, x: t.x, y: t.y });
                      }}
                      onKeyDown={(event) => {
                        if (!draggable) return;
                        const moves: Record<string, [number, number]> = {
                          ArrowLeft: [-1, 0],
                          ArrowRight: [1, 0],
                          ArrowUp: [0, -1],
                          ArrowDown: [0, 1],
                        };
                        const move = moves[event.key];
                        if (move) {
                          event.preventDefault();
                          onSelect?.(t);
                          const x = Math.max(
                            0,
                            Math.min(state.width - 1, t.x + move[0]),
                          );
                          const y = Math.max(
                            0,
                            Math.min(state.height - 1, t.y + move[1]),
                          );
                          if (x !== t.x || y !== t.y) onMove?.(t.id, x, y);
                        } else if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault();
                          onSelect?.(t);
                        }
                      }}
                    >
                      <circle
                        r={CELL / 2 - 3}
                        fill={SIDE_FILL[t.side] ?? SIDE_FILL.neutral}
                      />
                      <text className="mapcanvas__token-label" dy="4">
                        {initials(t.name)}
                      </text>
                      <title>
                        {t.name}
                        {t.tags.length ? ` — ${t.tags.join(', ')}` : ''}
                        {t.invisible ? ' (hidden from players)' : ''}
                      </title>
                      {t.tags.length ? (
                        <text
                          className="mapcanvas__token-tag"
                          dy={CELL / 2 + 8}
                        >
                          {t.tags.join(' · ')}
                        </text>
                      ) : null}
                    </g>
                  );
                })}
            </g>
          </svg>
        </div>
      </div>
      <div className="mapcanvas__controls" role="group" aria-label="Map zoom">
        <span>
          {state.width} × {state.height} squares
        </span>
        <button
          type="button"
          className="button button--ghost button--small"
          aria-label="Zoom out"
          disabled={zoom === 1}
          onClick={() => setZoom((z) => Math.max(1, z - 0.5))}
        >
          −
        </button>
        <button
          type="button"
          className="button button--ghost button--small"
          onClick={() => setZoom(1)}
          aria-label="Fit map"
        >
          {Math.round(zoom * 100)}%
        </button>
        <button
          type="button"
          className="button button--ghost button--small"
          aria-label="Zoom in"
          disabled={zoom === 3}
          onClick={() => setZoom((z) => Math.min(3, z + 0.5))}
        >
          +
        </button>
      </div>

      {state.features.length > 0 ? (
        <details className="mapcanvas__terrain-list">
          <summary>
            Terrain &amp; rules{' '}
            <span className="desk-count">{state.features.length}</span>
          </summary>
          <ol className="mapcanvas__feature-key" aria-label="Terrain">
            {state.features.map((feature, index) => (
              <li key={feature.id}>
                <button
                  type="button"
                  className={
                    hovered?.id === feature.id
                      ? 'mapcanvas__feature-button is-active'
                      : 'mapcanvas__feature-button'
                  }
                  aria-pressed={hovered?.id === feature.id}
                  onClick={() =>
                    setHovered((current) =>
                      current?.id === feature.id ? null : feature,
                    )
                  }
                >
                  <span className="mapcanvas__feature-number">{index + 1}</span>
                  <span>
                    <strong>{feature.name}</strong>
                    <small>{feature.effect}</small>
                  </span>
                </button>
              </li>
            ))}
          </ol>
        </details>
      ) : null}

      {hovered ? (
        <div className="mapcanvas__tip" role="status">
          <strong>{hovered.name}</strong>
          <span>{hovered.effect}</span>
        </div>
      ) : null}
    </div>
  );
}
